import { Server, Socket } from 'socket.io';
import { AuthSocket } from '../socket.bootstrap';
import { SocketEvents } from '@ichat/shared-types';
import { prisma } from '../../shared/prisma';

// Persistent in-memory waiting queue per meeting code
const meetingWaitingQueues = new Map<string, Map<string, {
  socketId: string;
  participantId: string;
  guestName: string;
  requestedAt: string;
  meetingCode: string;
}>>();

export function meetingHandler(io: Server, socket: AuthSocket) {
  const currentUserId = socket.userId || (socket as any).handshake?.auth?.userId || socket.id;
  const currentDisplayName = socket.displayName || (socket as any).handshake?.auth?.displayName || socket.username || 'Guest';

  // Helper to get room identifier
  const getMeetingRoom = (codeOrId: string) => `meeting:${codeOrId.toLowerCase().trim()}`;

  // 1. Join Request / Enter Room
  socket.on('meeting:join', async (data: {
    meetingCode: string;
    participantId: string;
    displayName?: string;
    avatarUrl?: string;
    role?: string;
    isGuest?: boolean;
    audioMuted?: boolean;
    videoOff?: boolean;
  }) => {
    const cleanCode = data.meetingCode.toLowerCase().trim();
    const room = getMeetingRoom(cleanCode);
    socket.join(room);

    const participantInfo = {
      socketId: socket.id,
      participantId: data.participantId || socket.id,
      userId: currentUserId,
      displayName: data.displayName || currentDisplayName,
      avatarUrl: data.avatarUrl || null,
      role: data.role || 'participant',
      isGuest: !!data.isGuest,
      audioMuted: !!data.audioMuted,
      videoOff: !!data.videoOff,
      handRaised: false,
      isSpotlight: false,
      isPinned: false,
      joinedAt: new Date().toISOString(),
    };

    // Broadcast to others in the meeting room
    socket.to(room).emit('meeting:participant_joined', participantInfo);

    // If joining user is host or existing participant, send any currently queued guests!
    const queue = meetingWaitingQueues.get(cleanCode);
    if (queue && queue.size > 0) {
      socket.emit('meeting:waiting_list', Array.from(queue.values()));
    }

    // Update DB status if meeting exists
    try {
      const meeting = await prisma.meeting.findFirst({
        where: { code: cleanCode },
      });
      if (meeting) {
        if (meeting.status === 'scheduled') {
          await prisma.meeting.update({
            where: { id: meeting.id },
            data: { status: 'live', actualStartTime: new Date() },
          });
        }
      }
    } catch (e) {
      console.warn('Could not update meeting live status in DB:', e);
    }
  });

  // 2. Waiting Room: Guest requests to join
  socket.on('meeting:request_entry', async (data: {
    meetingCode: string;
    guestName: string;
    participantId: string;
  }) => {
    const cleanCode = data.meetingCode.toLowerCase().trim();
    const room = getMeetingRoom(cleanCode);
    const waitingRoom = `meeting:waiting:${cleanCode}`;
    socket.join(waitingRoom);

    const payload = {
      socketId: socket.id,
      participantId: data.participantId || socket.id,
      guestName: data.guestName,
      requestedAt: new Date().toISOString(),
      meetingCode: data.meetingCode,
    };

    // Store in persistent queue so host sees it even if joining later
    if (!meetingWaitingQueues.has(cleanCode)) {
      meetingWaitingQueues.set(cleanCode, new Map());
    }
    meetingWaitingQueues.get(cleanCode)!.set(socket.id, payload);

    // Notify host(s) and existing participants in this room
    io.to(room).emit('meeting:guest_waiting', payload);

    // Also notify host personal room if host is online elsewhere
    try {
      const meeting = await prisma.meeting.findFirst({
        where: { code: cleanCode },
      });
      if (meeting && meeting.hostId) {
        io.to(`user:${meeting.hostId}`).emit('meeting:guest_waiting', payload);
        io.to(meeting.hostId).emit('meeting:guest_waiting', payload);
      }
    } catch (e) {}
  });

  // 3. Waiting Room: Host admits guest
  socket.on('meeting:admit_guest', (data: {
    meetingCode: string;
    targetSocketId: string;
    targetParticipantId?: string;
  }) => {
    const cleanCode = data.meetingCode.toLowerCase().trim();
    const waitingRoom = `meeting:waiting:${cleanCode}`;

    // Remove from in-memory waiting queue
    if (meetingWaitingQueues.has(cleanCode)) {
      meetingWaitingQueues.get(cleanCode)!.delete(data.targetSocketId);
    }

    if (data.targetSocketId) {
      io.to(data.targetSocketId).emit('meeting:admitted', {
        meetingCode: data.meetingCode,
      });
    }
    // Also emit to waiting room in case socket reconnected
    io.to(waitingRoom).emit('meeting:admitted', {
      meetingCode: data.meetingCode,
      targetSocketId: data.targetSocketId,
      targetParticipantId: data.targetParticipantId,
    });
  });

  // 4. Waiting Room: Host rejects guest
  socket.on('meeting:reject_guest', (data: {
    meetingCode: string;
    targetSocketId: string;
    reason?: string;
  }) => {
    const cleanCode = data.meetingCode.toLowerCase().trim();
    const waitingRoom = `meeting:waiting:${cleanCode}`;

    // Remove from in-memory waiting queue
    if (meetingWaitingQueues.has(cleanCode)) {
      meetingWaitingQueues.get(cleanCode)!.delete(data.targetSocketId);
    }

    if (data.targetSocketId) {
      io.to(data.targetSocketId).emit('meeting:rejected', {
        reason: data.reason || 'Host declined your entry request',
      });
    }
    io.to(waitingRoom).emit('meeting:rejected', {
      targetSocketId: data.targetSocketId,
      reason: data.reason || 'Host declined your entry request',
    });
  });

  // 5. Multi-Peer WebRTC Mesh Signaling
  socket.on('meeting:signal', (data: {
    meetingCode: string;
    toSocketId?: string;
    signal: any;
    fromParticipantId: string;
    fromSocketId?: string;
    isScreen?: boolean;
  }) => {
    const payload = {
      ...data,
      fromSocketId: socket.id,
      fromUserId: currentUserId,
      fromDisplayName: currentDisplayName,
    };

    if (data.toSocketId) {
      io.to(data.toSocketId).emit('meeting:signal', payload);
    } else {
      const room = getMeetingRoom(data.meetingCode);
      socket.to(room).emit('meeting:signal', payload);
    }
  });

  // 5.1 Screen Share Status Broadcast
  socket.on('meeting:screen_share', (data: {
    meetingCode: string;
    participantId: string;
    displayName: string;
    isScreenSharing: boolean;
  }) => {
    const room = getMeetingRoom(data.meetingCode);
    socket.to(room).emit('meeting:screen_share', {
      socketId: socket.id,
      participantId: data.participantId,
      displayName: data.displayName || currentDisplayName,
      isScreenSharing: data.isScreenSharing,
    });
  });

  // 6. Host Actions (Mute all, disable camera, kick, lock, toggle chat, etc.)
  socket.on('meeting:host_action', async (data: {
    meetingCode: string;
    action: 'mute' | 'mute_all' | 'disable_camera' | 'kick' | 'lock' | 'toggle_chat' | 'toggle_screenshare' | 'spotlight' | 'lower_hand' | 'end_meeting';
    targetSocketId?: string;
    targetParticipantId?: string;
    value?: any;
  }) => {
    const room = getMeetingRoom(data.meetingCode);

    if (data.action === 'end_meeting') {
      io.to(room).emit('meeting:ended_by_host', {
        endedBy: currentDisplayName,
      });
      try {
        await prisma.meeting.updateMany({
          where: { code: data.meetingCode.toLowerCase().trim() },
          data: { status: 'ended', actualEndTime: new Date() },
        });
      } catch (e) {}
      return;
    }

    if (data.action === 'kick' && data.targetSocketId) {
      io.to(data.targetSocketId).emit('meeting:kicked', {
        reason: 'Removed by meeting host',
      });
      io.to(room).emit('meeting:participant_left', {
        socketId: data.targetSocketId,
        participantId: data.targetParticipantId,
      });
      return;
    }

    if (data.targetSocketId) {
      io.to(data.targetSocketId).emit('meeting:host_action', data);
    } else {
      io.to(room).emit('meeting:host_action', data);
    }

    // Persist lock or permission overrides in DB
    try {
      if (data.action === 'lock') {
        await prisma.meeting.updateMany({
          where: { code: data.meetingCode.toLowerCase().trim() },
          data: { isLocked: !!data.value },
        });
      } else if (data.action === 'toggle_chat') {
        await prisma.meeting.updateMany({
          where: { code: data.meetingCode.toLowerCase().trim() },
          data: { allowChat: !!data.value },
        });
      } else if (data.action === 'toggle_screenshare') {
        await prisma.meeting.updateMany({
          where: { code: data.meetingCode.toLowerCase().trim() },
          data: { allowScreenShare: !!data.value },
        });
      }
    } catch (e) {}
  });

  // 7. Hand Raise
  socket.on('meeting:hand_raise', (data: {
    meetingCode: string;
    participantId: string;
    displayName: string;
    isRaised: boolean;
  }) => {
    const room = getMeetingRoom(data.meetingCode);
    io.to(room).emit('meeting:hand_raise', {
      socketId: socket.id,
      participantId: data.participantId,
      displayName: data.displayName || currentDisplayName,
      isRaised: data.isRaised,
      timestamp: Date.now(),
    });
  });

  // 8. Emoji Reactions (Floating hearts, thumbs up, etc.)
  socket.on('meeting:reaction', (data: {
    meetingCode: string;
    emoji: string;
    participantId: string;
    displayName?: string;
  }) => {
    const room = getMeetingRoom(data.meetingCode);
    io.to(room).emit('meeting:reaction', {
      emoji: data.emoji,
      senderName: data.displayName || currentDisplayName,
      participantId: data.participantId,
      timestamp: Date.now(),
    });
  });

  // 9. In-Meeting Chat Messages (Broadcast to others to prevent duplicate echo)
  socket.on('meeting:chat', (data: {
    meetingCode: string;
    id: string;
    senderId: string;
    senderName: string;
    avatarUrl?: string;
    text: string;
    timestamp: number;
  }) => {
    const room = getMeetingRoom(data.meetingCode);
    socket.to(room).emit('meeting:chat', data);
  });

  // 10. Live Captions / Subtitles Broadcast
  socket.on('meeting:caption', (data: {
    meetingCode: string;
    speakerName: string;
    speakerId: string;
    text: string;
    isFinal: boolean;
    timestamp: number;
  }) => {
    const room = getMeetingRoom(data.meetingCode);
    socket.to(room).emit('meeting:caption', data);
  });

  // 11. Live Transcript Segment Recording
  socket.on('meeting:transcript_entry', async (data: {
    meetingCode: string;
    entry: {
      id: string;
      speakerName: string;
      speakerId?: string;
      text: string;
      timestamp: number;
    };
  }) => {
    const room = getMeetingRoom(data.meetingCode);
    io.to(room).emit('meeting:transcript_entry', data.entry);

    try {
      const meeting = await prisma.meeting.findFirst({
        where: { code: data.meetingCode.toLowerCase().trim() },
        select: { id: true, transcript: true },
      });
      if (meeting) {
        const existing = Array.isArray(meeting.transcript) ? meeting.transcript : [];
        await prisma.meeting.update({
          where: { id: meeting.id },
          data: { transcript: [...existing, data.entry] },
        });
      }
    } catch (e) {}
  });

  // 12. Participant State Updates (Camera on/off, Mic mute/unmute, Screen sharing)
  socket.on('meeting:media_state', (data: {
    meetingCode: string;
    participantId: string;
    audioMuted?: boolean;
    videoOff?: boolean;
    screenSharing?: boolean;
  }) => {
    const room = getMeetingRoom(data.meetingCode);
    socket.to(room).emit('meeting:media_state', {
      socketId: socket.id,
      participantId: data.participantId,
      audioMuted: data.audioMuted,
      videoOff: data.videoOff,
      screenSharing: data.screenSharing,
    });
  });

  // 13. Participant Leave
  socket.on('meeting:leave', (data: { meetingCode: string; participantId: string }) => {
    const room = getMeetingRoom(data.meetingCode);
    socket.leave(room);
    socket.to(room).emit('meeting:participant_left', {
      socketId: socket.id,
      participantId: data.participantId,
      userId: currentUserId,
      displayName: currentDisplayName,
    });
  });

  // 14. Whiteboard Syncing inside Meeting
  socket.on(SocketEvents.WHITEBOARD_DRAW, (data: { roomId: string; drawingData: any }) => {
    socket.to(data.roomId).emit(SocketEvents.WHITEBOARD_DRAW, {
      roomId: data.roomId,
      userId: socket.userId,
      drawingData: data.drawingData,
    });
  });

  // 15. Disconnect Cleanup
  socket.on('disconnecting', () => {
    meetingWaitingQueues.forEach((queue) => {
      queue.delete(socket.id);
    });
  });

  socket.on(SocketEvents.WHITEBOARD_CLEAR, (data: { roomId: string }) => {
    socket.to(data.roomId).emit(SocketEvents.WHITEBOARD_CLEAR, {
      roomId: data.roomId,
      userId: socket.userId,
    });
  });

  socket.on('WHITEBOARD_CURSOR', (data: { roomId: string; cursor: any }) => {
    socket.to(data.roomId).emit('WHITEBOARD_CURSOR', {
      roomId: data.roomId,
      userId: socket.userId,
      cursor: data.cursor,
    });
  });
}
